import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {DisplayMode, type IssueCreatePayload} from '..'
import {instanceOfContactLinkData, instanceOfIssueTemplateData, instanceOfBlankIssueData} from './model'
import {type ConstructIssueCreateParamsType, constructIssueCreateParams} from './template-args'
import type {RepositoryPickerRepository$data as Repository} from '@github-ui/item-picker/repository'
import {URLS} from '../constants'
import {reservedQueryKeys} from '../constants/urls'

export const issueNewPathFromExisting = (path: string): string => {
  const keyword = '/issues'
  const newPath = '/issues/new'

  // Remove any query string or hash from the path
  const cleanPath = path.split(/[?#]/)[0]!
  // Find the last occurrence of the keyword
  const lastIndexOf = cleanPath.lastIndexOf(keyword)
  if (lastIndexOf === -1) {
    // If the keyword is not found, return the new path
    return newPath
  }

  // Replace the keyword and anything after it with the new path
  return cleanPath.substring(0, lastIndexOf) + newPath
}

export const removeChooseFromBasePath = (path: string): string => {
  // If the path explicitly ends with /choose or /choose/, remove it.
  // Otherwise, return the path as-is.
  return path.replace(/\/choose\/?$/, '')
}

type setTemplateUrlParamIfAppropriateType = {
  insidePortal: boolean
  template: IssueCreatePayload
  repository: Repository | undefined
  preselectedRepository: Repository | undefined
}
export const setTemplateUrlParamIfAppropriate = ({
  insidePortal,
  ...props
}: setTemplateUrlParamIfAppropriateType): void => {
  if (insidePortal) {
    return
  }

  const newSearchComponent = constructTemplateUrlParamsIfAppropriate({
    displayMode: DisplayMode.TemplatePicker,
    ...props,
  })

  const cleanPathWithoutChoose = removeChooseFromBasePath(ssrSafeWindow?.location?.pathname || '')
  ssrSafeWindow?.history?.replaceState(
    {},
    '',
    `${cleanPathWithoutChoose}?${newSearchComponent}${ssrSafeWindow?.location?.hash}`,
  )
}

type constructTemplateUrlParamsIfAppropriateType = {
  displayMode: DisplayMode
  template: IssueCreatePayload | undefined
  repository: Repository | undefined
  preselectedRepository: Repository | undefined
} & ConstructIssueCreateParamsType

export const constructTemplateUrlParamsIfAppropriate = ({
  displayMode,
  template,
  repository,
  preselectedRepository,
  title,
  body,
  assignees,
  labels,
  projects,
  milestone,
  type,
}: constructTemplateUrlParamsIfAppropriateType): string => {
  // If we had a preselected repo, then we assume there is no need to include the repository in the URL
  const sharedProps = {
    repository,
    includeRepository: preselectedRepository === undefined,
    templateFileName: template?.fileName,
  }

  if (!template || displayMode === DisplayMode.TemplatePicker || instanceOfContactLinkData(template.data)) {
    return constructIssueCreateParams({...sharedProps})
  }

  const titleParam = template.data.title !== title ? title : undefined
  let bodyParam = undefined
  if (instanceOfIssueTemplateData(template.data) || instanceOfBlankIssueData(template.data)) {
    bodyParam = template.data.body !== body ? body : undefined
  }

  // TODO We currently don't do a smart "diff" for labels, assignees or projects, so we always include them in the URL.
  const assigneesParam = assignees && assignees.length > 0 ? assignees : undefined
  const labelsParam = labels && labels.length > 0 ? labels : undefined
  const projectsParam = projects && projects.length > 0 ? projects : undefined
  const milestoneParam = milestone ?? undefined
  const typeParam = type ?? undefined

  return constructIssueCreateParams({
    ...sharedProps,
    title: titleParam,
    body: bodyParam,
    assignees: assigneesParam,
    labels: labelsParam,
    projects: projectsParam,
    milestone: milestoneParam,
    type: typeParam,
  })
}

export const getPotentialFormDefaultValuesFromUrl = (urlParams?: URLSearchParams): Record<string, string> => {
  const searchParams = urlParams ?? new URLSearchParams(ssrSafeWindow?.location?.search || '')
  const defaultValuesById: Record<string, string> = {}

  for (const [key, value] of searchParams) {
    if (reservedQueryKeys.includes(key)) {
      continue
    } else if (value === null) {
      defaultValuesById[key] = ''
    } else {
      defaultValuesById[key] = value.substring(0, URLS.maxQueryLengthLimits.body)
    }
  }

  return defaultValuesById
}

export const safeStringParamGet = (
  searchParams: URLSearchParams,
  key: string,
  maxLimit: number,
): string | undefined => {
  const value = searchParams.get(key)
  if (value === null) {
    return undefined
  }

  return value.substring(0, maxLimit)
}

export const safeStringParamSet = (
  searchParams: URLSearchParams,
  key: string,
  value: string | undefined,
  maxLimit: number,
): void => {
  if (value === undefined) {
    return
  }

  searchParams.set(key, value.substring(0, maxLimit))
}

export const safeStringArrayParamSet = (
  searchParams: URLSearchParams,
  key: string,
  values: string[] | undefined,
  maxLengthLimit: number,
): void => {
  if (!values) {
    return
  }

  searchParams.set(key, values.slice(0, maxLengthLimit).join(','))
}
